import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ProveedorSesion } from './lib/sesion';
import { Rutas } from './rutas';
import { Marco } from './componentes/Marco';
import { AvisoDeFalloGlobal } from './componentes/AvisoDeFallo';
import { ApiError } from './lib/api';
import { anunciarFallo } from './lib/errores';
import './estilos.css';

/* ════════════════════════════════════════════════════════════════════════════
   NINGUNA ACCIÓN FALLA EN SILENCIO · las dos redes (2026-09-03)

   La usuaria: «varios usuarios realizan alguna acción —cargar una negociación,
   una planificación, una devolución— y luego no queda cargada y tampoco les
   sale ningún mensaje de error».

   El aviso se dispara AQUÍ y no en las pantallas, por el mismo motivo por el
   que el apunte en la bitácora se hace en `json()` y no en los 62 endpoints:
   puesto en cada sitio, el próximo se olvida — y este olvido no se ve, porque
   el usuario se queda sin mensaje y nadie se entera. Las pantallas siguen
   pintando su error donde lo pintaban; esto es la red que garantiza que además
   llegue a los ojos de quien pulsó.

   ── RED 1 · el `onError` global de las mutaciones ───────────────────────────
   `MutationCache` avisa de TODA mutación que falla, y lo hace ADEMÁS del
   `onError` propio de la mutación y AUNQUE NO TENGA NINGUNO — que es el caso de
   `marcar` en Planificación Coordinadores y de una de las cuatro de GoToMarket.
   Cubre las 18 pantallas que escriben de una vez.

   Solo mutaciones, nunca consultas, y la distinción es deliberada: hay lecturas
   que se tragan su error A PROPÓSITO y anunciarlas sería una alarma falsa a
   cada tecla — el autocompletado de producto de Devoluciones («el campo sigue
   siendo manual, y sin avisos falsos»), la tasa BCV de Cartera y pagos, que ya
   dice lo suyo en el propio campo, y el `me()` del arranque, que contesta 401
   cuando todavía no ha entrado nadie.

   ── RED 2 · las promesas que nadie recoge ───────────────────────────────────
   Una escritura lanzada a mano, sin `.catch` y sin pasar por una mutación, se
   perdía sin dejar nada: es `PlanCoordinadores.tsx:979`, el botón «Solicitar
   autorización». `unhandledrejection` la recoge. Filtra por `ApiError` para no
   convertir en aviso de portal un fallo de programación, que no es asunto de
   quien está trabajando, y no llama a `preventDefault()`: el error sigue
   saliendo por la consola, que es donde se diagnostica.
   ════════════════════════════════════════════════════════════════════════════ */

/* Los ya anunciados por la red 1, para que la red 2 no los repita: una
 * `mutateAsync` sin `catch` pasa por las dos, y la segunda vuelta llegaría SIN
 * saber que escribía —el `ApiError` no lleva el método— y borraría de la
 * pantalla el renglón de «¿quedó guardado?», que es el más útil del aviso.
 * `WeakSet` y no un array: no retiene los errores ni hay que vaciarlo. */
const yaAnunciados = new WeakSet<object>();

const mutationCache = new MutationCache({
  onError: (error, _vars, _ctx, mutation) => {
    if (!(error instanceof ApiError)) return;
    yaAnunciados.add(error);
    /* `meta.accion` es lo que la pantalla declara que estaba haciendo, en las
     * palabras de la persona («Guardar la planificación»). Sin ella el aviso
     * dice «La acción no se completó», que sigue siendo infinitamente mejor que
     * el silencio de hoy. */
    const meta = mutation.options.meta as { accion?: string } | undefined;
    anunciarFallo({
      status: error.status,
      mensaje: error.message,
      codigo: error.codigo,
      detalle: error.detalle,
      /* `navigator.onLine` se lee ahora y no cuando se armó el error: es un
       * instante después, y para lo único que se usa —distinguir «no salió del
       * equipo» de «salió y no sé qué pasó»— basta. */
      enLinea: typeof navigator === 'undefined' ? undefined : navigator.onLine,
      ...(meta?.accion ? { accion: meta.accion } : {}),
      /* Una mutación ESCRIBE: es lo que habilita el renglón de «¿quedó
       * guardado?», que no se puede deducir de un `ApiError` a secas. */
      escritura: true,
    });
  },
});

window.addEventListener('unhandledrejection', (ev) => {
  const e: unknown = ev.reason;
  if (!(e instanceof ApiError)) return;
  if (yaAnunciados.has(e)) return;
  anunciarFallo({
    status: e.status,
    mensaje: e.message,
    codigo: e.codigo,
    detalle: e.detalle,
    enLinea: typeof navigator === 'undefined' ? undefined : navigator.onLine,
  });
});

const qc = new QueryClient({
  mutationCache,
  defaultOptions: {
    queries: {
      staleTime: 5 * 60_000,   // los datos del portal cambian por refresco diario
      gcTime: 30 * 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const root = document.getElementById('root');
if (!root) throw new Error('Falta #root en index.html');

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={qc}>
      <BrowserRouter>
        <ProveedorSesion>
          <Marco>
            <Rutas />
          </Marco>
          {/* Fuera del `Marco` a propósito: va fijo sobre el viewport y no debe
              heredar ni el ancho ni el desplazamiento de la página. */}
          <AvisoDeFalloGlobal />
        </ProveedorSesion>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
